import { createSlice } from '@reduxjs/toolkit';
import { logout } from './authSlice';

/**
 * The `runInvestmentAnalysis` jobs this session started, by key — Ki_UI's
 * `analysisJobs`, plus the analysis a finished job produced.
 *
 * A job is started by a screen and outlives it. Proposals keeps one entry per
 * portfolio, because leaving its Portfolio tab unmounts the panel that started
 * the run: without this, coming back abandoned a job still running on the
 * server and dropped a finished plan nobody had saved yet. With it, the next
 * mount follows the same job to its end, or shows the finished plan again, and
 * its Save still has something to save.
 *
 * `{ [key]: { jobId, variables, startedAt, urlId, finishedAt } }` — `variables`
 * are the ones the run was started with (a poll must send them again); `urlId`
 * is null until the job is done. Times come in the payload, so the reducer
 * stays pure. Entries are dropped by age where they are read
 * (`useProposalRun`), not by a timer here, and all of them on sign-out.
 */
const initialState = {};

const analysisJobsSlice = createSlice({
  name: 'analysisJobs',
  initialState,
  reducers: {
    /** A job is running for `key`: `{ key, jobId, variables, at }`. Replaces what the key held. */
    analysisJobStarted(state, { payload }) {
      const {
        key, jobId, variables = null, at = null,
      } = payload || {};
      if (!key || !jobId) return;
      state[key] = {
        jobId, variables, startedAt: at, urlId: null, finishedAt: null,
      };
    },
    /** The job for `key` is done and published under `urlId`: `{ key, urlId, jobId, variables, at }`. */
    analysisJobFinished(state, { payload }) {
      const {
        key, urlId, jobId = null, variables = null, at = null,
      } = payload || {};
      if (!key || !urlId) return;
      const held = state[key];
      state[key] = {
        jobId: jobId ?? held?.jobId ?? null,
        variables: variables ?? held?.variables ?? null,
        startedAt: held?.startedAt ?? at,
        urlId,
        finishedAt: at,
      };
    },
    /** Forget `key`: its job failed, or its analysis was saved or set aside. */
    analysisJobCleared(state, { payload }) {
      if (payload?.key) delete state[payload.key];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  },
});

export const { analysisJobStarted, analysisJobFinished, analysisJobCleared } = analysisJobsSlice.actions;

/** The entry for `key`, or null. */
export const selectAnalysisJob = (state, key) => (key ? state.analysisJobs?.[key] ?? null : null);

export default analysisJobsSlice.reducer;
